import React, { useState } from "react";
import { Box, Typography, InputBase, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const getInitials = (name = "") => {
  const p = name.trim().split(" ");
  return p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

const COLORS = ["#28B3B4", "#D6DF27", "#4a9960", "#7a5abd", "#c88a3a"];
const avatarColor = (name = "") => COLORS[((name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0)) % COLORS.length];

const Step2Associate = ({ associates, selected, onSelect, selectedPosition, loading }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const [search, setSearch] = useState("");

  const filtered = associates.filter((a) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      a.fullName?.toLowerCase().includes(q) ||
      a.skills?.some((s) => s.skillName?.toLowerCase().includes(q))
    );
  });

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: "40px" }}>
      <CircularProgress size={20} sx={{ color: t.accent }} />
    </Box>
  );

  return (
    <Box>
      {/* Selected position summary bar */}
      {selectedPosition && (
        <Box sx={{
          background: t.surface2, border: `1px solid ${t.border2}`,
          borderRadius: "3px", padding: "10px 14px", mb: "16px",
        }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.14em", textTransform: "uppercase", color: t.text3, mb: "4px" }}>
            Selected Position
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
            <Box>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.accent, mb: "1px" }}>
                {selectedPosition.positionCode}
              </Typography>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12.5px", fontWeight: 600, color: t.text }}>
                {selectedPosition.roleTitle}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: t.text3 }}>
                {selectedPosition.sowName}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: t.text3 }}>
                {selectedPosition.accountName}
              </Typography>
            </Box>
            {selectedPosition.billedFte != null && (
              <Box sx={{
                background: selectedPosition.positionType === "BIL" ? t.accent2 : t.gold2,
                border: `1px solid ${selectedPosition.positionType === "BIL" ? t.accent + "44" : t.gold + "44"}`,
                borderRadius: "2px", padding: "2px 8px",
                display: "flex", alignItems: "center", gap: "5px", flexShrink: 0,
              }}>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, letterSpacing: "0.08em" }}>
                  BILLED FTE
                </Typography>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", fontWeight: 700, color: selectedPosition.positionType === "BIL" ? t.accent : t.gold }}>
                  {selectedPosition.billedFte}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Search */}
      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.15em", textTransform: "uppercase", color: t.text3, mb: "8px" }}>
        Select Associate
      </Typography>
      <Box sx={{
        display: "flex", alignItems: "center", gap: "7px",
        background: t.surface2, border: `1px solid ${t.border2}`,
        borderRadius: "3px", padding: "7px 10px", mb: "10px",
        "&:focus-within": { borderColor: t.accent },
      }}>
        <Typography sx={{ color: t.text3, fontSize: "11px" }}>⌕</Typography>
        <InputBase
          placeholder="Search by name or skill..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            flex: 1, fontFamily: "'Inter', sans-serif",
            fontSize: "12px", color: t.text,
            "& input::placeholder": { color: t.text3 },
          }}
        />
      </Box>

      {/* Associate cards */}
      {filtered.map((a) => {
        const isSelected = selected?.associateId === a.associateId;
        const color      = avatarColor(a.fullName);
        const usedPct    = Math.round((a.usedFte / 1.0) * 100);
        const shownSkills = (a.skills || []).slice(0, 3).map(s => s.skillName ?? s);
const extraSkills = (a.skills || []).length - 3;

        return (
          <Box
            key={a.associateId}
            onClick={() => onSelect(a)}
            sx={{
              display: "flex", alignItems: "center", gap: "12px",
              border: isSelected ? `1px solid ${t.accent}88` : `1px solid ${t.border2}`,
              background: isSelected ? t.accent2 : t.surface2,
              borderRadius: "3px", padding: "10px 12px",
              mb: "5px", cursor: "pointer", transition: "all 0.15s",
              "&:hover": { borderColor: t.accent + "44" },
            }}
          >
            {/* Avatar */}
            <Box sx={{
              width: "34px", height: "34px", borderRadius: "50%", flexShrink: 0,
              background: `${color}22`, border: `1px solid ${color}55`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'DM Mono', monospace", fontSize: "10px", fontWeight: 700, color,
            }}>
              {getInitials(a.fullName)}
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600, color: t.text }}>
                {a.fullName}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: t.text3, mb: "4px" }}>
                Level {a.levelCode}
                {a.levelTitle ? ` · ${a.levelTitle}` : ""}
              </Typography>
              {/* Skills */}
              <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {shownSkills.map((sk) => (
                  <Box key={sk} sx={{ background: t.surface3, border: `1px solid ${t.border2}`, borderRadius: "2px", padding: "1px 7px" }}>
                    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: t.text2 }}>{sk}</Typography>
                  </Box>
                ))}
                {extraSkills > 0 && (
                  <Box sx={{ background: t.surface3, borderRadius: "2px", padding: "1px 7px" }}>
                    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3 }}>+{extraSkills}</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* FTE */}
            <Box sx={{ flexShrink: 0, textAlign: "right" }}>
              <Box sx={{ width: "70px", height: "4px", background: t.surface3, borderRadius: "2px", overflow: "hidden", mb: "4px" }}>
                <Box sx={{ height: "100%", width: `${usedPct}%`, background: usedPct >= 75 ? t.gold : t.accent, borderRadius: "2px" }} />
              </Box>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
                {a.usedFte}
              </Typography>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.accent }}>
                {a.availableFte} available
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default Step2Associate;